/**
 * DocumentService
 *
 * Manages document uploads, metadata, and approval workflow for person profiles.
 * Extends BaseContentService for standardized 10-step integration pattern.
 *
 * Features:
 * - Firebase Storage upload for documents
 * - Approval workflow integration
 * - Transcription support
 * - Comprehensive metadata management
 * - SystemConfig validation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  QueryConstraint,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import {
  Document,
  DocumentType,
  DocumentMetadata,
  DocumentFilters,
  DocumentApprovalStatus,
} from '../types/document';
import { SystemConfig } from '../types';

import {
  BaseContentService,
  BaseContentEntity,
  CreateContentOptions,
} from './baseContentService';
import { SystemConfigService } from './systemConfigService';
import { ChangeTrackingService } from './changeTrackingService';
import { AuditLogService } from './auditLogService';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Document entity extending base content entity
 */
export interface DocumentEntity extends BaseContentEntity {
  // Association
  personName: string;

  // Document metadata
  title: string;
  type: DocumentType;
  description?: string;
  date?: string;
  location?: string;
  source?: string;
  sourceUrl?: string;
  tags: string[];

  // File information
  fileName: string;
  storageURL: string;
  thumbnailURL?: string;
  fileSize: number;
  mimeType: string;

  // Transcription
  transcription?: string;
  transcribedBy?: string;
  transcribedAt?: Date;

  // Moderation
  approvalStatus: DocumentApprovalStatus;
  approvedBy?: string;
  approvedAt?: Date;
  rejectionReason?: string;

  // Metadata
  uploadedBy: string;
  uploadedByName: string;

  // Soft delete
  deletedBy?: string;
  deletedAt?: Date;
}

/**
 * Document creation data (before Firebase Storage upload)
 */
interface DocumentCreationData {
  personId: string;
  personName: string;
  title: string;
  type: DocumentType;
  description?: string;
  date?: string;
  location?: string;
  source?: string;
  sourceUrl?: string;
  tags: string[];
  fileName: string;
  storageURL: string;
  thumbnailURL?: string;
  fileSize: number;
  mimeType: string;
  uploadedByName: string;
}

// ============================================================================
// DocumentService Class
// ============================================================================

/**
 * DocumentService - Manages document operations
 * Extends BaseContentService for 10-step compliance
 */
class DocumentServiceImpl extends BaseContentService<DocumentEntity> {
  // ============================================================================
  // STATIC CONSTANTS
  // ============================================================================

  private static readonly STORAGE_PATH_PREFIX = 'documents';

  // ============================================================================
  // ABSTRACT PROPERTIES IMPLEMENTATION
  // ============================================================================

  protected collectionName = 'documents';
  protected featureFlagKey: keyof SystemConfig['features'] = 'enableDocumentsTab';
  protected approvalConfigKey: keyof SystemConfig['content'] = 'requireDocumentApproval';
  protected maxPerPersonConfigKey: keyof SystemConfig['content'] = 'maxDocumentsPerPerson';
  protected authorFieldName = 'uploadedBy'; // Documents use uploadedBy as author field
  protected changeTrackingEntityType = 'document';

  // ============================================================================
  // ABSTRACT METHODS IMPLEMENTATION
  // ============================================================================

  /**
   * Validate document-specific data
   */
  protected async validateContent(data: DocumentCreationData, config: SystemConfig): Promise<void> {
    // Validate file size
    const maxSizeMB = config.content.maxDocumentSizeMB || 10;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (data.fileSize > maxSizeBytes) {
      throw new Error(`File size exceeds maximum allowed size of ${maxSizeMB}MB`);
    }

    // Validate file type
    const fileExtension = data.fileName.split('.').pop()?.toLowerCase() || '';
    const allowedTypes = config.content.allowedDocumentTypes || ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'tiff'];

    if (!allowedTypes.includes(fileExtension)) {
      throw new Error(`File type .${fileExtension} is not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }

    // Validate required fields
    if (!data.title || !data.type) {
      throw new Error('title and type are required');
    }

    if (!data.personId) {
      throw new Error('personId is required for document upload');
    }
  }

  /**
   * Build Firestore query constraints from filters
   */
  protected buildFilters(filters: DocumentFilters): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];

    if (filters.type) {
      constraints.push(where('type', '==', filters.type));
    }

    if (filters.approvalStatus) {
      constraints.push(where('approvalStatus', '==', filters.approvalStatus));
    }

    if (filters.uploadedBy) {
      constraints.push(where('uploadedBy', '==', filters.uploadedBy));
    }

    return constraints;
  }

  /**
   * Map Firestore document data to typed entity
   */
  protected mapDocumentToEntity(docData: any, docId: string): DocumentEntity {
    return {
      id: docId,
      personId: docData.personId,
      personName: docData.personName,
      title: docData.title,
      type: docData.type,
      description: docData.description,
      date: docData.date,
      location: docData.location,
      source: docData.source,
      sourceUrl: docData.sourceUrl,
      tags: docData.tags || [],
      fileName: docData.fileName,
      storageURL: docData.storageURL,
      thumbnailURL: docData.thumbnailURL,
      fileSize: docData.fileSize,
      mimeType: docData.mimeType,
      transcription: docData.transcription,
      transcribedBy: docData.transcribedBy,
      transcribedAt: docData.transcribedAt ? this.timestampToDate(docData.transcribedAt) : undefined,
      approvalStatus: docData.approvalStatus,
      approvedBy: docData.approvedBy,
      approvedAt: docData.approvedAt ? this.timestampToDate(docData.approvedAt) : undefined,
      rejectionReason: docData.rejectionReason,
      uploadedBy: docData.uploadedBy,
      uploadedByName: docData.uploadedByName,
      createdAt: this.timestampToDate(docData.createdAt),
      updatedAt: this.timestampToDate(docData.updatedAt),
      createdBy: docData.createdBy || docData.uploadedBy, // Fallback for old data
      isApproved: docData.isApproved,
      isDeleted: docData.isDeleted,
      deletedBy: docData.deletedBy,
      deletedAt: docData.deletedAt ? this.timestampToDate(docData.deletedAt) : undefined,
    };
  }

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Upload a document to Firebase Storage and create Firestore record
   * Follows complete 10-step integration pattern
   */
  async uploadDocument(
    file: File,
    personId: string,
    personName: string,
    metadata: DocumentMetadata,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<Document> {
    try {
      // Validate file type
      if (!file.type) {
        throw new Error('File type could not be determined');
      }

      // Generate unique filename
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2);
      const fileExtension = file.name.split('.').pop() || 'pdf';
      const fileName = `${timestamp}_${randomId}.${fileExtension}`;

      // Create storage reference
      const storagePath = `${DocumentServiceImpl.STORAGE_PATH_PREFIX}/${personId}/${randomId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, file);
      const snapshot = await uploadTask;
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Generate thumbnail URL for image documents
      const thumbnailURL = this.isImageType(file.type) ? downloadURL : undefined;

      // Prepare document data
      const documentData: DocumentCreationData = {
        personId,
        personName,
        title: metadata.title,
        type: metadata.type,
        description: metadata.description,
        date: metadata.date,
        location: metadata.location,
        source: metadata.source,
        sourceUrl: metadata.sourceUrl,
        tags: metadata.tags || [],
        fileName,
        storageURL: downloadURL,
        thumbnailURL,
        fileSize: file.size,
        mimeType: file.type,
        uploadedByName: userName,
      };

      // Execute 10-step pattern via BaseContentService
      const createdEntity = await this.executeCreatePattern(
        documentData,
        userId,
        userName,
        userRole,
        {
          requiresApproval: true, // Will be determined by config
          entityTypeName: 'document',
          skipNotification: false,
        }
      );

      // Convert to Document type with additional fields
      const document: Document = {
        ...createdEntity,
        approvalStatus: createdEntity.isApproved ? 'approved' : 'pending',
      } as unknown as Document;

      return document;

    } catch (error: any) {
      console.error('Error uploading document:', error);
      throw new Error(`Failed to upload document: ${error.message}`);
    }
  }

  /**
   * Get all documents for a specific person
   */
  async getDocumentsByPerson(
    personId: string,
    filters?: DocumentFilters
  ): Promise<Document[]> {
    try {
      const entities = await this.getByPerson(personId, filters);
      return entities.map(entity => ({
        ...entity,
        approvalStatus: entity.isApproved ? 'approved' : 'pending',
      })) as unknown as Document[];
    } catch (error: any) {
      console.error('Error getting documents:', error);
      throw new Error(`Failed to get documents: ${error.message}`);
    }
  }

  /**
   * Get a specific document by ID
   */
  async getDocumentById(documentId: string): Promise<Document | null> {
    try {
      const entity = await this.getById(documentId);
      if (!entity) {
        return null;
      }

      return {
        ...entity,
        approvalStatus: entity.isApproved ? 'approved' : 'pending',
      } as unknown as Document;
    } catch (error: any) {
      console.error('Error getting document:', error);
      throw new Error(`Failed to get document: ${error.message}`);
    }
  }

  /**
   * Update document metadata
   */
  async updateDocument(
    documentId: string,
    updates: Partial<DocumentMetadata>,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      // Get existing document
      const docRef = doc(db, this.collectionName, documentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Document not found');
      }

      const existingDoc = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

      // Check permissions (owner or admin)
      if (!this.checkOwnerOrAdmin(existingDoc, userId, userRole)) {
        throw new Error('You do not have permission to update this document');
      }

      // Prepare update data
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Update in Firestore
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, updateData);

      // Get updated document for change tracking
      const updatedSnap = await getDoc(docRef);
      const updatedDoc = this.mapDocumentToEntity(updatedSnap.data()!, updatedSnap.id);

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'document',
          entityId: documentId,
          changeType: 'update',
          beforeState: existingDoc,
          afterState: updatedDoc,
        },
        userId,
        userName,
        true
      );

      // Log audit
      await AuditLogService.logAction({
        userId,
        action: 'content_approved',
        contentType: 'document',
        contentId: documentId,
        details: {
          operation: 'update',
          userName,
          personId: existingDoc.personId,
          updates,
          documentTitle: existingDoc.title,
        },
      });

    } catch (error: any) {
      console.error('Error updating document:', error);
      throw new Error(`Failed to update document: ${error.message}`);
    }
  }

  /**
   * Soft delete a document
   */
  async deleteDocument(
    documentId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      // Use BaseContentService soft delete (handles change tracking and audit logging)
      await this.softDelete(documentId, userId, userName, userRole);
    } catch (error: any) {
      console.error('Error deleting document:', error);
      throw new Error(`Failed to delete document: ${error.message}`);
    }
  }

  /**
   * Approve a pending document (admin only)
   */
  async approveDocument(
    documentId: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      // Use BaseContentService approve method
      await this.approve(documentId, userId, userName, 'admin');

      // Update approval status field (Document-specific)
      const docRef = doc(db, this.collectionName, documentId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        approvalStatus: 'approved',
        updatedAt: serverTimestamp(),
      });

    } catch (error: any) {
      console.error('Error approving document:', error);
      throw new Error(`Failed to approve document: ${error.message}`);
    }
  }

  /**
   * Reject a pending document (admin only)
   */
  async rejectDocument(
    documentId: string,
    reason: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      // Use BaseContentService reject method
      await this.reject(documentId, reason, userId, userName, 'admin');

      // Update approval status field (Document-specific)
      const docRef = doc(db, this.collectionName, documentId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        approvalStatus: 'rejected',
        updatedAt: serverTimestamp(),
      });

    } catch (error: any) {
      console.error('Error rejecting document:', error);
      throw new Error(`Failed to reject document: ${error.message}`);
    }
  }

  /**
   * Get all documents awaiting approval (admin only)
   */
  async getDocumentsAwaitingApproval(limit?: number): Promise<Document[]> {
    try {
      const entities = await this.getAwaitingApproval(limit || 50);
      return entities.map(entity => ({
        ...entity,
        approvalStatus: 'pending' as DocumentApprovalStatus,
      })) as unknown as Document[];
    } catch (error: any) {
      console.error('Error getting pending documents:', error);
      throw new Error(`Failed to get pending documents: ${error.message}`);
    }
  }

  /**
   * Add transcription to a document
   */
  async addTranscription(
    documentId: string,
    transcription: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const config = await SystemConfigService.getConfig();

      if (!config.content.enableDocumentTranscription) {
        throw new Error('Document transcription is currently disabled');
      }

      const docRef = doc(db, this.collectionName, documentId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        transcription,
        transcribedBy: userId,
        transcribedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'document',
          entityId: documentId,
          changeType: 'update',
          beforeState: null,
          afterState: null,
        },
        userId,
        userName,
        true
      );

    } catch (error: any) {
      console.error('Error adding transcription:', error);
      throw new Error(`Failed to add transcription: ${error.message}`);
    }
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Check if MIME type is an image
   */
  private isImageType(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

/**
 * Singleton instance of DocumentService
 */
export const DocumentService = new DocumentServiceImpl();
